import { NonEmptyArray, Option, flow, pipe, ReadonlyArray } from "fp";
import React from "react";

import * as Data from "~/Data";
import * as Driver from "~/Driver";
import * as Log from "~/Log";
import { NotApplicable } from "~/NotApplicable";
import * as Table from "~/Table";
import { Time } from "~/Time";

const colSpan = () => 6 as const;

export const Component = (route: Log.Route) => (
  <Table.Component>
    <Head.Component />
    <Body.Component {...route} />
  </Table.Component>
);

namespace Head {
  export const Component = () => (
    <Table.Head.Component>
      <Table.Row.Component>
        <Table.Header.Component>Status</Table.Header.Component>
        <Table.Header.Component>Time</Table.Header.Component>
        <Table.Header.Component>Origin</Table.Header.Component>
        <Table.Header.Component>Source</Table.Header.Component>
        <Table.Header.Component>Location</Table.Header.Component>
      </Table.Row.Component>
    </Table.Head.Component>
  );
}

namespace Body {
  export const constrainEventsToTargetDay =
    ({ dateTime }: Time.DateTime, time: Time.Zone) =>
    (events: readonly Log.Audit.Event.Event[]) => {
      const constraintInterval = Time.DateTime.toDayInterval(
        { dateTime },
        time
      );
      return events.filter((event) =>
        pipe(constraintInterval, Time.Interval.Stopped.collides(event))
      );
    };

  export const Component = (route: Log.Route) => {
    const defaultZone = Time.Zone.use();
    const driverZone = Driver.useFromReference({
      ...Option.toUndefined(Driver.useReferenceFromRoute()),
      found: (driver) => driver?.time ?? defaultZone,
      missing: () => defaultZone,
    });
    return Data.Remote.when(Log.Audit.useSortedEventsFromActiveLog(route), {
      loading: () => <Table.Wide.Loading.Component colSpan={colSpan()} />,
      error: (error) => (
        <Table.Wide.ErrorMessage.Component colSpan={colSpan()}>
          {error.message}
        </Table.Wide.ErrorMessage.Component>
      ),
      success: flow(
        NonEmptyArray.fromReadonlyArray,
        Option.fold(
          () => <Table.Wide.Empty.Component colSpan={colSpan()} />,
          (events) => (
            <Rows.Component
              events={pipe(
                events,
                constrainEventsToTargetDay(route, driverZone)
              )}
            />
          )
        )
      ),
    });
  };
}

namespace Rows {
  export const Component: React.Fn<{
    readonly events: readonly Log.Audit.Event.Event[];
  }> = ({ events }) =>
    pipe(
      events,
      ReadonlyArray.mapWithIndex((index, event) => (
        <Row.Component key={index} {...event} />
      )),
      (rows) => <Table.Body.Component>{rows}</Table.Body.Component>
    );
}

namespace Row {
  export const Component: React.Fn<Log.Audit.Event.Event> = (event) => (
    <Table.Row.Component>
      <Log.Table.Status.Component {...event} />
      <Table.Column.Component>
        <Time.Interval {...event} />
      </Table.Column.Component>
      <Created {...event} />
      <Log.Table.Source.Component {...event} />
      <Log.Table.Location.Component {...event} />
    </Table.Row.Component>
  );
}

function Created({ created }: Partial<Log.Audit.Event.Created>) {
  return (
    <Table.Column.Component>
      {created ? (
        <Log.Audit.Event.Created created={created} />
      ) : (
        <NotApplicable />
      )}
    </Table.Column.Component>
  );
}
